import { MetaBulletStylesConfig } from '../types/fabric-extensions';

/**
 * Дефолтная конфигурация стилей для всех типов TextEntity
 * Эти стили используются, если пользователь не передал свою конфигурацию
 */
export const DEFAULT_TEXT_ENTITY_STYLES: MetaBulletStylesConfig = {
  // Заголовки
  title: {
    fontSize: 32,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    lineHeight: 1.2,
    marginBottom: 24,
    marginLeft: 0,
    textAlign: 'left',
    fill: '#000000',
  },

  subtitle: {
    fontSize: 24,
    fontFamily: 'Arial, sans-serif',
    fontWeight: '600',
    lineHeight: 1.3,
    marginBottom: 20,
    marginLeft: 0,
    textAlign: 'left',
    fill: '#000000',
  },

  'heading-1': {
    fontSize: 28,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    lineHeight: 1.25,
    marginBottom: 22,
    marginLeft: 0,
    textAlign: 'left',
    fill: '#000000',
  },

  'heading-2': {
    fontSize: 22,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    lineHeight: 1.3,
    marginBottom: 18,
    marginLeft: 0,
    textAlign: 'left',
    fill: '#000000',
  },

  'heading-3': {
    fontSize: 18,
    fontFamily: 'Arial, sans-serif',
    fontWeight: '600',
    lineHeight: 1.35,
    marginBottom: 16,
    marginLeft: 0,
    textAlign: 'left',
    fill: '#000000',
  },

  // Буллиты разных уровней (минимальный уровень = 1)
  'bullet-level-1': {
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    lineHeight: 1.4,
    marginBottom: 12,
    marginLeft: 0, // Уровень 1 - без дополнительного отступа
    textAlign: 'left',
    fill: '#000000',
  },

  'bullet-level-2': {
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    lineHeight: 1.4,
    marginBottom: 12,
    marginLeft: 20, // (2-1) * 20px
    textAlign: 'left',
    fill: '#000000',
  },

  'bullet-level-3': {
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    lineHeight: 1.4,
    marginBottom: 12,
    marginLeft: 40, // (3-1) * 20px
    textAlign: 'left',
    fill: '#000000',
  },

  'bullet-level-4': {
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    lineHeight: 1.4,
    marginBottom: 12,
    marginLeft: 60, // (4-1) * 20px
    textAlign: 'left',
    fill: '#000000',
  },

  // Digital-accent стили
  'digital-accent': {
    digit: {
      fontSize: 36,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      lineHeight: 1.2,
      marginBottom: 16,
      marginLeft: 0,
      textAlign: 'left',
      fill: '#2563eb',
      spacingTop: 0,
      spacingBottom: 0.5,
    },
    superscript: {
      fontSize: 18, // 50% от digit fontSize
      offsetX: 8, // отступ слева от digit
      offsetY: 'top' as const, // выравнивание по вертикали ('top' | 'center' | 'bottom' | 'centered-by-first-line' | number)
      fontWeight: 'normal',
      fontFamily: 'Arial, sans-serif',
      fill: '#64748b',
      lineHeight: 1.2,
    },
  },

  'digital-accent-superscript': {
    fontSize: 18,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    lineHeight: 1.2,
    marginBottom: 0,
    marginLeft: 0,
    textAlign: 'left',
    fill: '#64748b',
  },
};

/**
 * Получить объединенную конфигурацию стилей
 * Объединяет пользовательские стили с дефолтными
 */
export function mergeStylesConfig(
  customStyles?: Partial<MetaBulletStylesConfig>,
): MetaBulletStylesConfig {
  if (!customStyles) {
    return { ...DEFAULT_TEXT_ENTITY_STYLES };
  }

  const result = { ...DEFAULT_TEXT_ENTITY_STYLES };

  // Перебираем все типы и объединяем пользовательские стили с дефолтными
  Object.keys(result).forEach((type) => {
    const entityType = type as keyof MetaBulletStylesConfig;
    if (customStyles[entityType]) {
      if (entityType === 'digital-accent') {
        // Для digital-accent нужно объединять вложенные объекты digit и superscript
        const defaultDigitalAccent = result[entityType] as unknown as {
          digit: Record<string, unknown>;
          superscript: Record<string, unknown>;
        };
        const customDigitalAccent = customStyles[entityType] as unknown as {
          digit?: Record<string, unknown>;
          superscript?: Record<string, unknown>;
        };

        result[entityType] = {
          digit: {
            ...defaultDigitalAccent.digit,
            ...customDigitalAccent.digit,
          },
          superscript: {
            ...defaultDigitalAccent.superscript,
            ...customDigitalAccent.superscript,
          },
        } as unknown as MetaBulletStylesConfig['digital-accent'];
      } else {
        result[entityType] = {
          ...result[entityType],
          ...customStyles[entityType],
        };
      }
    }
  });

  // Добавляем пользовательские стили, которых нет в дефолтном конфиге
  Object.keys(customStyles).forEach((type) => {
    const entityType = type as keyof MetaBulletStylesConfig;
    if (!result[entityType]) {
      // @ts-expect-error - сложные типы для digital-accent
      result[entityType] = customStyles[entityType];
    }
  });

  return result;
}
